import React, { useState, useRef, useEffect } from 'react';
import Button from '../../components/elements/button';
import { Send, Robot,  Clock } from '../../assets/icons';
import '../../assets/css/pages/ai_chat.css';
import { useNavigate } from 'react-router-dom';
import { messageApi } from '../../network/apis/message';
import { useAuth } from '../../context/AuthContext';
import AccessDenied from '../../components/elements/AccessDenied';
import LoadingSpinner from '../../components/elements/LoadingSpinner';

const WELCOME_MESSAGE = {
    type: 'ai',
    sender_role: 'bot',
    content: "Hello! I'm Beere, your AI aquaculture diagnostic assistant. How can I help you today?",
    timestamp: new Date()
};

const AiChat = () => {
    const { isAuthenticated } = useAuth();
    const [messages, setMessages] = useState([WELCOME_MESSAGE]);
    const [chatHistory, setChatHistory] = useState([]);
    const [selectedChat, setSelectedChat] = useState(null);
    const [randomChatId, setRandomChatId] = useState(Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15));
    const [input, setInput] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    const [isLoadingChat, setIsLoadingChat] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
    const chatEndRef = useRef(null);
    const navigate = useNavigate();

    // Load chat history
    useEffect(() => {
        const loadChatHistory = async () => {
            if (!isAuthenticated) return;
            
            setIsLoadingHistory(true);
            try {
                const response = await messageApi.getConversations(); 
                if (response.status === "unauthorized") {
                    console.error('Unauthorized access. Please log in again.');
                    return;
                }
                if (response.status.toString().toLowerCase() === "success" && response.data) {
                    const data = response.data;
                    const filteredData = data.filter(chat => !chat.chat_id.startsWith('case'));
                    setChatHistory(filteredData);
                    localStorage.setItem('chatHistory', JSON.stringify(filteredData));
                } else {
                    console.error('Error fetching chat history:', response.statusText);
                }
            } catch (error) {
                console.error('Error fetching chat history:', error);
            } finally {
                setIsLoadingHistory(false);
            }
        };
        loadChatHistory();
    }, [isAuthenticated]);

    const scrollToBottom = () => {
        chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    const saveChatHistory = (messages) => {
        const newChat = {
            id: Date.now(),
            title: messages[1]?.content.substring(0, 30) + '...' || 'New Chat',
            messages: messages,
            timestamp: new Date()
        };

        setChatHistory(prev => {
            const updated = [newChat, ...prev];
            localStorage.setItem('chatHistory', JSON.stringify(updated));
            return updated;
        });
    };

    const loadChat = async (chat) => {
        // setSelectedChat(chat);
        // setMessages(chat.messages);
        setIsLoadingChat(true);
        try {
            const response = await messageApi.getMessages({"chat_id": chat.chat_id});
            if (response.status.toString().toLowerCase() === "success" && response.data) {
                const data = response.data;
                setMessages(data);
                setSelectedChat(chat);
                setIsMobileSidebarOpen(false);
            } else {
                console.error('Error fetching chat:', response.statusText);
            }
        }
        catch (error) {
            console.error('Error fetching chat:', error);
        } finally {
            setIsLoadingChat(false);
        }
    };

    const startNewChat = async () => {
        setSelectedChat(null);
        setRandomChatId(Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15));
        setMessages([WELCOME_MESSAGE]);
        setIsMobileSidebarOpen(false);
    };

    useEffect(() => {
        
        scrollToBottom();
    }, [messages]);

    const handleSend = async () => {
        if (!input.trim() || isSending) return;

        const userMessage = {
            type: 'user',
            sender_role: 'farmer',
            content: input
        };

        // setMessages(prev => [...prev, userMessage]);
        const updatedMessages = [...messages, userMessage];
        setMessages(updatedMessages);
        setInput('');
        setIsTyping(true);
        setIsSending(true);

        if (!selectedChat) {
            try {
                messageApi.newConversation({ chat_id: randomChatId, title: input.substring(0, 10) + '...', participants: [1] }).then((_response) => {
                    setSelectedChat({ chat_id: randomChatId })
                }
                ).then(async () => {
                    try {
            const response = await messageApi.newMessage({ chat_id: randomChatId, content:input, ask_ai:true });
            if (response.status.toString().toLowerCase() !== "success") {
                console.error('Error sending message:', response.statusText);
                setIsTyping(false);
            } else {
                const data = response.data;
                const aiResponse = {
                    type: 'ai',
                    sender_role: 'bot',
                    content: data.bot_response.content,
                };
                const finalMessages = [...updatedMessages, aiResponse];
                setMessages(finalMessages);
                setIsTyping(false);

                saveChatHistory(finalMessages);
                 
                return;
            }
        } catch (error) {
            console.error('Error:', error);
            setIsTyping(false);
        } finally {
            setIsSending(false);
        }
                });

            } catch (error) {
                console.error('Error creating new chat:', error);
            }
        } else {

        try {
            const response = await messageApi.newMessage({ chat_id: selectedChat.chat_id, content:input, ask_ai:true });
            if (response.status.toString().toLowerCase() !== "success") {
                console.error('Error sending message:', response.statusText);
                setIsTyping(false);
            } else {
                const data = response.data;
                const aiResponse = {
                    type: 'ai',
                    sender_role: 'bot',
                    content: data.bot_response.content,
                };
                const finalMessages = [...updatedMessages, aiResponse];
                setMessages(finalMessages);
                setIsTyping(false);
                 // Save to chat history if this is a new chat
                 
                return;
            }
        } catch (error) {
            console.error('Error:', error);
            setIsTyping(false);
        } finally {
            setIsSending(false);
        }
    }
    };

    // Render authentication check
    if (!isAuthenticated) {
        return <AccessDenied />;
    }

    return (
        <div className={`ai-chat-page ${isMobileSidebarOpen ? 'sidebar-open' : ''}`}>
            {isMobileSidebarOpen && (
                <button
                    type="button"
                    className="sidebar-backdrop"
                    onClick={() => setIsMobileSidebarOpen(false)}
                    aria-label="Close chat history"
                />
            )}

             <div className="chat-sidebar">
                <div className="sidebar-header">
                    <Button 
                        text="New Chat"
                        variant="primary"
                        onClick={startNewChat}
                        fullWidth
                    />
                </div>

                <div className="chat-history">
                    <h3>Chat History</h3>
                    {isLoadingHistory ? (
                        <div className="loading-history">
                            <LoadingSpinner />
                        </div>
                    ) : chatHistory.length === 0 ? (
                        <div className="empty-history">
                            <p>No chat history yet</p>
                        </div>
                    ) : (
                        chatHistory.map(chat => (
                            <div 
                                key={chat.chat_id || chat.id}
                                className={`history-item ${selectedChat?.chat_id === chat.chat_id ? 'active' : ''}`}
                                onClick={() => loadChat(chat)}
                            >
                                <span className="history-title">{chat.title}</span>
                                <span className="history-time">
                                    <img src={Clock} alt="time" />
                                    {new Date(chat.updated_at).toLocaleDateString()}
                                </span>
                            </div>
                        ))
                    )}
                </div>

                <div className="sidebar-section">
                    <h3>Quick Actions</h3>
                    <Button 
                        text="Request Aquatic Vet Doctor"
                        variant="primary"
                        onClick={() => navigate('/doctors')}
                    />
                    <Button 
                        text="View Reported Cases"
                        variant="outline"
                        onClick={() => navigate('/cases')}
                    />
                </div>
            </div>

            <div className="chat-container">
                <div className="chat-header">
                    <div className="chat-header-top">
                        <Button
                            text="History"
                            variant="outline"
                            className="history-toggle-btn"
                            onClick={() => setIsMobileSidebarOpen(true)}
                        />
                    </div>
                    <div className="ai-profile">
                        <img src={Robot} alt="" className="ai-avatar" />
                        <div className="ai-info">
                            <h2>Beere</h2>
                            <p><small>AI-powered Aquaculture Diagnostic Assistant</small></p>
                        </div>
                    </div>
                </div>

                <div className="messages-container">
                    {isLoadingChat ? (
                        <div className="loading-chat">
                            <LoadingSpinner />
                        </div>
                    ) : messages.map((message, index) => (
                        <div key={index} className={`message-wrapper ${message.sender_role?.toLowerCase()}`}>
                        <div className="message">
                            <div className="message-avatar">
                                {message.sender_role?.toLowerCase() === 'bot' ? (
                                    <div className="avatar bot-avatar">
                                        <img src={Robot} alt="AI Assistant" />
                                    </div>
                                ) : (
                                    // <div className="avatar user-avatar">
                                    //     <img src={User} alt="User" />
                                    // </div>
                                    <></>
                                )}
                            </div>
                            <div className="message-bubble">
                                {message.sender_role?.toLowerCase() === 'bot' && (
                                    <div className="sender-name">Beere</div>
                                )}
                                <div className="message-content" style={{ whiteSpace: 'pre-wrap' }}>
                                    {message.content}
                                </div>
                                <div className="message-time">
                                    {message.timestamp ? new Date(message.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : ''}
                                </div>
                            </div>
                        </div>
                    </div>
                    ))}
                    {isTyping && (
                        <div className="typing-indicator">
                            <div className="typing-dots">
                                <span></span>
                                <span></span>
                                <span></span>
                            </div>
                            Beere is typing...
                        </div>
                    )}
                    <div ref={chatEndRef} />
                </div>

                <div className="chat-input">
                    <textarea
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        placeholder="Describe your fish health concerns..."
                        onKeyPress={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleSend();
                            }
                        }}
                    />
                    <Button
                        icon={<img src={Send} alt="" />}
                        onClick={handleSend}
                        disabled={isTyping || isSending || !input.trim()}
                        loading={isSending}
                    />
                </div>
            </div>
        </div>
    );
};

export default AiChat;